<template>
    <div class="main-content px-4">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-4 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0 flex-nowrap">
                    <li class="breadcrumb-item">
                        <router-link class="nav-link active d-inline" to="/">Início</router-link>
                    </li>
                    <li class="breadcrumb-item">
                        <router-link class="nav-link active d-inline" to="/inventory">Inventários</router-link>
                    </li>
                    <li class="breadcrumb-item active text-dark small d-inline" aria-current="page">
                        Contagem Física
                    </li>
                </ol>
            </nav>
        </header>

        <div v-if="inventoryData && inventoryData.id">

            <div class="card p-4 shadow-subtle rounded-4 border-0 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <div>
                        <h4 class="fw-bold text-dark mb-1">Inventário: {{ inventoryData.description }}</h4>
                        <p class="text-muted small mb-0">
                            <i class="bi bi-geo-alt me-1"></i> {{ branchName }} |
                            <span :class="getStatusBadge(status)">
                                STATUS: {{ translateStatus(status) }}
                            </span>
                        </p>
                    </div>

                    <div class="d-flex gap-2">
                        <button class="btn btn-pmg-light shadow-sm border d-flex align-items-center gap-2"
                            @click="syncSystemicStock" :disabled="status === 'ADJUSTED'"
                            title="Atualizar o estoque sistêmico com a foto atual do banco">
                            <i class="bi bi-arrow-repeat" :class="{ 'fa-spin': loadingSync }"></i>
                            {{ loadingSync ? 'Sincronizando...' : 'Sincronizar' }}
                        </button>
                        <div class="dropdown">
                            <button class="btn btn-pmg-light shadow-sm border dropdown-toggle" type="button"
                                data-bs-toggle="dropdown">
                                <i class="bi bi-download me-2"></i>Exportar
                            </button>
                            <ul class="dropdown-menu shadow-lg border-0 rounded-4">
                                <li>
                                    <a class="dropdown-item py-2" href="#" @click.prevent="downloadReport('pdf')">
                                        <i class="bi bi-file-pdf text-danger me-2"></i>Relatório PDF
                                    </a>
                                </li>
                                <li>
                                    <a class="dropdown-item py-2" href="#" @click.prevent="downloadReport('excel')">
                                        <i class="bi bi-file-excel text-success me-2"></i>Planilha Excel
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <button v-if="status === 'OPEN'" class="btn btn-pmg-primary shadow-sm px-4"
                            @click="confirmFinalize">
                            <i class="bi bi-check2-all me-2"></i>Finalizar Ajustes
                        </button>
                    </div>
                </div>

                <div class="row g-3 align-items-center">
                    <div class="col-md-12">
                        <label class="form-label small fw-bold text-muted mb-1">Bipar Código de Barras / SKU</label>
                        <div class="input-group">
                            <span class="input-group-text bg-light border-end-0 text-muted px-3"
                                style="min-width: 55px; justify-content: center;">
                                <i class="bi bi-barcode fs-5"></i>
                            </span>

                            <input ref="barcodeInput" v-model="barcode" @keyup.enter="handleBarcodeSearch"
                                class="form-control border-start-0 py-2" placeholder="Aguardando leitura do scanner..."
                                autofocus :disabled="status !== 'OPEN'">

                            <button class="btn btn-pmg-light shadow-sm border d-flex align-items-center gap-2"
                                type="button" @click="handleBarcodeSearch" :disabled="status !== 'OPEN' || !barcode">
                                <i class="bi bi-search me-2"></i>Consultar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card shadow-subtle rounded-4 border-0 overflow-hidden">
                <div class="table-responsive">
                    <table class="table table-pmg align-middle mb-0">
                        <thead class="bg-light">
                            <tr>
                                <th class="ps-4 py-3 small fw-bold text-muted text-uppercase">Produto / SKU</th>
                                <th class="py-3 small fw-bold text-muted text-center text-uppercase">Sistêmico</th>
                                <th class="py-3 small fw-bold text-muted text-center text-uppercase"
                                    style="width: 140px;">Físico</th>
                                <th class="py-3 small fw-bold text-muted text-center text-uppercase">Diferença</th>
                                <th class="pe-4 py-3 small fw-bold text-muted text-end text-uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in inventoryItems" :key="item.id"
                                :class="{ 'bg-success-subtle': item.counted }">
                                <td class="ps-4">
                                    <div class="fw-bold text-dark">{{ item.product.name }}</div>
                                    <div class="text-muted" style="font-size: 0.75rem;">SKU: {{ item.product.sku }}
                                    </div>
                                </td>

                                <td class="text-center text-secondary fw-bold fs-6">
                                    {{ item.system_stock }}
                                </td>

                                <td class="text-center">
                                    <input :id="'input-qty-' + item.id" type="number"
                                        v-model.number="item.physical_stock"
                                        class="form-control form-control-sm text-center fw-bold border-2 rounded-3"
                                        :class="item.counted ? 'border-success bg-white' : (item.is_editing ? 'border-primary' : 'bg-light')"
                                        @input="calculateDiff(item)" @keyup.enter="confirmSingleAdjust(item)"
                                        :disabled="status !== 'OPEN' || item.counted" placeholder="-">
                                </td>

                                <td class="text-center">
                                    <span :class="getDiffClass(item.difference)">
                                        {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                                    </span>
                                </td>

                                <td class="pe-4 text-end">
                                    <div class="d-flex justify-content-end align-items-center gap-2">
                                        <button v-if="item.counted && status === 'OPEN'"
                                            class="btn btn-sm btn-outline-secondary border-0 p-1"
                                            @click="resetItem(item)" title="Refazer ajuste">
                                            <i class="bi bi-arrow-counterclockwise"></i>
                                        </button>

                                        <button v-if="!item.counted"
                                            class="btn btn-sm btn-success px-3 rounded-pill fw-bold"
                                            @click="confirmSingleAdjust(item)"
                                            :disabled="item.physical_stock === null || status !== 'OPEN'">
                                            <i class="bi bi-check-lg me-1"></i>Ajustar
                                        </button>

                                        <span v-else class="badge rounded-pill bg-success px-3 py-2">
                                            <i class="bi bi-check-all me-1"></i>Ajustado
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div v-else class="card p-5 shadow-subtle rounded-4 border-0 text-center mt-4">
            <div v-if="loading" class="py-5">
                <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;"></div>
                <h5 class="text-dark fw-bold">A carregar dados da auditoria...</h5>
                <p class="text-muted small">Isto pode demorar alguns segundos dependendo do tamanho do inventário.</p>
            </div>
            <div v-else class="py-5">
                <i class="bi bi-exclamation-octagon text-danger mb-3" style="font-size: 4rem;"></i>
                <h4 class="fw-bold text-dark">Inventário não localizado</h4>
                <p class="text-muted">A rota atual não possui um código válido ou o inventário solicitado já não existe.
                </p>
                <router-link to="/inventory" class="btn btn-pmg-light border mt-4 px-5 py-2">
                    <i class="bi bi-arrow-left me-2"></i> Voltar para Lista de Inventários
                </router-link>
            </div>
        </div>
    </div>

    <div class="modal fade" id="confirmFinalizeModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4 text-center p-4">
                <div class="modal-body p-0">
                    <div class="text-warning mb-3">
                        <i class="bi bi-exclamation-triangle-fill" style="font-size: 3.5rem;"></i>
                    </div>
                    <h5 class="fw-bold text-dark mb-3">Finalizar Ajustes?</h5>
                    <p class="text-muted small mb-4">
                        <strong>Atenção:</strong> Este processo gerará entradas e saídas automáticas para igualar o
                        sistema ao estoque físico contado.<br>Deseja prosseguir?
                    </p>
                </div>
                <div class="d-flex gap-2 justify-content-center pb-2">
                    <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                    <button class="btn btn-pmg-primary px-4 rounded-3" @click="executeFinalize">
                        <i class="bi bi-check2-all me-1"></i> Confirmar Ajustes
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    name: 'InventoryCount',
    data() {
        return {
            inventoryId: null,
            inventoryData: {},
            branchName: '',
            status: '',
            barcode: '',
            inventoryItems: [],
            alerts: [],
            loading: true,
            loadingSync: false,
        }
    },
    created() {
        // Captura o ID da URL antes do componente ser montado
        this.inventoryId = this.$route.params.id;
    },
    mounted() {
        // Validação de segurança: o ID deve existir e não ser a string literal 'undefined'
        if (this.inventoryId && this.inventoryId !== 'undefined') {
            this.fetchInventoryData();
        } else {
            this.loading = false;
        }
    },
    methods: {
        async fetchInventoryData() {
            this.loading = true;
            try {
                const res = await api.get(`/inventories/${this.inventoryId}`);
                const data = res.data.data || res.data;

                if (data) {
                    this.inventoryData = data;
                    this.status = data.status;
                    this.branchName = data.branch?.name || 'Unidade não identificada';
                    this.inventoryItems = (data.items || []).map(item => {
                        if (data.status === 'ADJUSTED') {
                            return {
                                ...item,
                                difference: item.physical_stock - item.system_stock
                            };
                        }
                        return {
                            ...item,
                            physical_stock: null,
                            difference: 0
                        };
                    });
                }
            } catch (e) {
                console.error(e);
                this.showAlert("Não foi possível carregar os dados deste inventário.", "danger");
            } finally {
                this.loading = false;
            }
        },



        calculateDiff(item) {
            item.difference = (item.physical_stock || 0) - item.system_stock;
        },

        getDiffClass(diff) {
            if (diff === 0) return 'text-muted small fw-bold';
            return diff > 0
                ? 'badge bg-success-subtle text-success border-0 px-3 py-2'
                : 'badge bg-danger-subtle text-danger border-0 px-3 py-2';
        },

        resetItem(item) {
            item.physical_stock = 0;
            this.calculateDiff(item);
        },

        async downloadReport(format) {
            this.showAlert(`A gerar relatório ${format.toUpperCase()}...`, 'success');
            try {
                const response = await api.get(`/inventories/${this.inventoryId}/export`, {
                    params: { format },
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `divergencias_${this.inventoryId}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
                document.body.appendChild(link);
                link.click();
            } catch (e) {
                this.showAlert("Erro ao gerar relatório. Tente novamente.", "danger");
            }
        },

        confirmFinalize() {
            const modal = new window.bootstrap.Modal(document.getElementById('confirmFinalizeModal'));
            modal.show();
        },

        async processFinalize() {
            const pendingItems = this.inventoryItems.filter(i =>
                !i.counted &&
                i.physical_stock !== null &&
                i.physical_stock !== ''
            ).map(i => ({
                id: i.id,
                physical_stock: Number(i.physical_stock)
            }));

            try {
                // O endpoint /adjust processará os itens restantes e fechará o inventário
                await api.post(`/inventories/${this.inventoryId}/adjust`, {
                    items: pendingItems
                });

                this.showAlert("Inventário finalizado com sucesso!", "success");

                // Recarrega os dados para atualizar o status global para 'ADJUSTED'
                // Isso bloqueará os inputs e mostrará os selos de finalizado na tela.
                await this.fetchInventoryData();

            } catch (error) {
                let errorMessage = "Erro ao finalizar o inventário. Verifique a conexão.";

                if (error.response && error.response.data) {
                    if (error.response.data.errors) {
                        errorMessage = Object.values(error.response.data.errors)[0][0];
                    } else if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    }
                }

                this.showAlert(errorMessage, "danger");
            }
        },

        async executeFinalize() {
            // Esconde o modal de confirmação
            const modalEl = document.getElementById('confirmFinalizeModal');
            if (modalEl) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                if (modalInstance) {
                    modalInstance.hide();
                }
            }

            // Chama o processo real
            await this.processFinalize();
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = type === 'danger' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill';
            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), 8000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        getStatusBadge(status) {
            const styles = {
                'FROZEN': 'badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2',
                'ADJUSTED': 'badge bg-success-subtle text-success border border-success-subtle px-3 py-2',
                'CANCELLED': 'badge bg-secondary-subtle text-secondary border border-secondary-subtle px-3 py-2',
                'OPEN': 'badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-2'
            };
            return styles[status] || 'badge bg-light text-dark border px-3 py-2';
        },

        translateStatus(status) {
            const map = {
                'FROZEN': 'Congelado',
                'ADJUSTED': 'Ajustado',
                'OPEN': 'Aberto',
                'CANCELLED': 'Cancelado'
            };
            return map[status] || status;
        },

        // Dentro de methods:
        async syncSystemicStock() {
            this.loadingSync = true;
            try {
                await api.post(`/inventories/${this.inventoryId}/sync`);

                this.showAlert("Estoque sistêmico atualizado com sucesso!", "success");

                // Recarrega os dados para atualizar a coluna "Sistêmico" e as "Diferenças"
                await this.fetchInventoryData();
            } catch (e) {
                console.error(e);
                this.showAlert("Erro ao sincronizar estoque. Tente novamente.", "danger");
            } finally {
                this.loadingSync = false;
            }
        },

        handleBarcodeSearch() {
            const code = this.barcode.trim();
            const item = this.inventoryItems.find(i =>
                i.product.sku === code || i.product.barcode === code
            );

            if (item) {
                item.is_editing = true; // Habilita o input
                this.showAlert(`Produto "${item.product.name}" localizado. Informe a quantidade.`, 'info');

                // Foca automaticamente no input de quantidade desse item
                this.$nextTick(() => {
                    const el = document.getElementById(`input-qty-${item.id}`);
                    if (el) el.focus();
                });
            }
            this.barcode = '';
        },

        // Quando clica no botão "Ajustar" ou dá Enter
        async confirmSingleAdjust(item) {
            if (item.physical_stock === null) return;

            try {
                await api.post(`/inventories/${this.inventoryId}/adjust-item`, {
                    item_id: item.id,
                    physical_stock: item.physical_stock
                });

                item.counted = true;
                item.is_editing = false;
                item.system_stock = item.physical_stock; // Sincroniza visualmente
                this.calculateDiff(item);

                this.showAlert("Estoque ajustado e produto liberado!", "success");
            } catch (e) {
                this.showAlert("Erro ao processar ajuste individual.", "danger");
            }
        }
    }
}
</script>

<style scoped>
/* Padrão visual rigoroso (PMG) da Dailypag */
.main-content {
    background: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
}

.shadow-subtle {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02) !important;
}

/* Formulários */
.form-control-pmg {
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.form-control-pmg:focus {
    background: #fff;
    border-color: #561569;
    box-shadow: 0 0 0 3px rgba(33, 51, 97, 0.1);
}

.form-control-sm {
    border-radius: 8px;
}

/* Botões PMG */
.btn-pmg-primary {
    background: #561569;
    color: white;
    border-radius: 10px;
    font-weight: 600;
    padding: 0.6rem 1.2rem;
    transition: all 0.3s ease;
}

.btn-pmg-primary:hover {
    background: #1a284c;
    color: white;
}

.btn-pmg-light {
    background: white;
    color: #64748b;
    border-radius: 10px;
    font-weight: 600;
    padding: 0.6rem 1.2rem;
    transition: all 0.3s ease;
}

.btn-pmg-light:hover {
    background: #f1f5f9;
    color: #561569;
}

/* Transições das Toasts */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* Correção visual do Breadcrumb */
.breadcrumb-item+.breadcrumb-item::before {
    color: #cbd5e1;
}

.nav-link.active {
    font-weight: 600;
    color: #64748b !important;
    text-decoration: none;
}

/* Tabela PMG */
.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
}

.table-pmg tbody td {
    padding: 1rem 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.table-pmg tbody tr:hover {
    background-color: #f8fafc;
}

.badge {
    font-size: 0.75rem;
    font-weight: 700;
}

.fa-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>